<template>
  <div class="ss" :class="{ 'is-open': open, 'is-disabled': disabled }" v-click-outside-ss="close">
    <div class="ss-control" :class="{ errors: hasError }" @click="toggle">
      <input
        ref="input"
        type="text"
        class="ss-input"
        :value="open ? query : selectedLabel"
        :placeholder="open ? (searchPlaceholder || placeholder) : placeholder"
        :disabled="disabled"
        autocomplete="off"
        @focus="onFocus"
        @input="onType"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="choose(filtered[active])"
        @keydown.esc.prevent="close"
        @keydown.tab="close"
      />
      <span class="ss-clear" v-if="clearable && hasValue && !disabled" @click.stop="clear">&times;</span>
      <span class="ss-caret"></span>
    </div>

    <div class="ss-menu" v-show="open">
      <div class="ss-empty" v-if="!filtered.length">No match for "{{ query }}"</div>
      <div
        v-else
        class="ss-option"
        v-for="(o, i) in filtered"
        :key="'ss' + i"
        :class="{ 'is-active': i === active, 'is-selected': isSelected(o) }"
        @mouseenter="active = i"
        @mousedown.prevent="choose(o)"
      >
        <span class="ss-option-label">{{ o.label }}</span>
        <span class="ss-option-note" v-if="o.note">{{ o.note }}</span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * A searchable single-select.
 *
 * Written by hand rather than pulling in vue-select / multiselect, because the project
 * takes no new npm packages. It is deliberately small: a text input that doubles as the
 * search box, a filtered list, and arrow/enter/escape handling.
 *
 * `options` is a plain array. Each entry is normalised to { value, label, note }, so the
 * caller can pass the API's country rows straight through without reshaping them.
 */

/** Registered locally so no global directive is added to the app. */
const clickOutside = {
  bind: function (el, binding) {
    el.__ssOutside = function (event) {
      if (el !== event.target && !el.contains(event.target)) { binding.value(event); }
    };
    document.addEventListener("click", el.__ssOutside);
    document.addEventListener("touchstart", el.__ssOutside);
  },
  unbind: function (el) {
    document.removeEventListener("click", el.__ssOutside);
    document.removeEventListener("touchstart", el.__ssOutside);
    delete el.__ssOutside;
  },
};

export default {
  name: "SearchSelect",
  directives: { "click-outside-ss": clickOutside },
  props: {
    value: { default: "" },
    options: { type: Array, default: function () { return []; } },
    /** Property to read the option's value from. */
    valueKey: { type: String, default: "value" },
    /** Property to read the option's visible text from. */
    labelKey: { type: String, default: "label" },
    /** Optional secondary text shown on the right of each row, e.g. "+977". */
    noteKey: { type: String, default: "" },
    /** Extra fields the search should match on, beyond the label. */
    searchKeys: { type: Array, default: function () { return []; } },
    placeholder: { type: String, default: "Select" },
    searchPlaceholder: { type: String, default: "Type to search..." },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
  },
  data() {
    return { open: false, query: "", active: 0 };
  },
  computed: {
    normalised: function () {
      const vm = this;
      return (this.options || []).map(function (o) {
        if (o === null || o === undefined) { return { value: "", label: "", note: "", haystack: "" }; }
        if (typeof o !== "object") {
          return { value: o, label: String(o), note: "", haystack: String(o).toLowerCase() };
        }
        const label = String(o[vm.labelKey] === undefined ? "" : o[vm.labelKey]);
        const note = vm.noteKey ? String(o[vm.noteKey] === undefined ? "" : o[vm.noteKey]) : "";
        const extra = vm.searchKeys.map(function (k) { return o[k] === undefined ? "" : o[k]; });
        return {
          value: o[vm.valueKey],
          label: label,
          note: note,
          raw: o,
          haystack: (label + " " + note + " " + extra.join(" ")).toLowerCase(),
        };
      });
    },
    filtered: function () {
      const q = String(this.query || "").trim().toLowerCase();
      if (!q) { return this.normalised; }
      // Entries that START with the term rank first - typing "ne" should offer Nepal
      // before Netherlands only because of position, not alphabetically.
      const starts = [], contains = [];
      this.normalised.forEach(function (o) {
        const at = o.haystack.indexOf(q);
        if (at === 0) { starts.push(o); }
        else if (at > 0) { contains.push(o); }
      });
      return starts.concat(contains);
    },
    hasValue: function () {
      return this.value !== "" && this.value !== null && this.value !== undefined;
    },
    selected: function () {
      const vm = this;
      const hit = this.normalised.filter(function (o) { return String(o.value) === String(vm.value); });
      return hit.length ? hit[0] : null;
    },
    selectedLabel: function () {
      if (!this.selected) { return ""; }
      return this.selected.note
        ? this.selected.label + " (" + this.selected.note + ")"
        : this.selected.label;
    },
  },
  watch: {
    // Keep the highlight inside the list as the search narrows it.
    filtered: function () { this.active = 0; },
  },
  methods: {
    isSelected(o) {
      return this.hasValue && String(o.value) === String(this.value);
    },
    toggle() {
      if (this.disabled) { return; }
      if (this.open) { this.close(); return; }
      this.openMenu();
    },
    onFocus() {
      if (!this.disabled) { this.openMenu(); }
    },
    openMenu() {
      this.open = true;
      this.query = "";
      this.active = Math.max(0, this.filtered.findIndex ? this.filtered.findIndex(this.isSelected) : 0);
      if (this.active < 0) { this.active = 0; }
      const vm = this;
      this.$nextTick(function () { if (vm.$refs.input) { vm.$refs.input.select(); } });
    },
    close() {
      this.open = false;
      this.query = "";
    },
    onType(e) {
      this.open = true;
      this.query = e.target.value;
    },
    move(step) {
      if (!this.open) { this.openMenu(); return; }
      const n = this.filtered.length;
      if (!n) { return; }
      this.active = (this.active + step + n) % n;
      this.scrollActiveIntoView();
    },
    scrollActiveIntoView() {
      const vm = this;
      this.$nextTick(function () {
        const menu = vm.$el.querySelector(".ss-menu");
        const row = menu && menu.querySelectorAll(".ss-option")[vm.active];
        if (!menu || !row) { return; }
        if (row.offsetTop < menu.scrollTop) { menu.scrollTop = row.offsetTop; }
        else if (row.offsetTop + row.offsetHeight > menu.scrollTop + menu.clientHeight) {
          menu.scrollTop = row.offsetTop + row.offsetHeight - menu.clientHeight;
        }
      });
    },
    choose(o) {
      if (!o) { return; }
      this.$emit("input", o.value);
      this.$emit("change", o.raw === undefined ? o.value : o.raw);
      this.close();
    },
    clear() {
      this.$emit("input", "");
      this.$emit("change", null);
      this.close();
    },
  },
};
</script>

<style scoped>
.ss { position: relative; width: 100%; }
.ss-control { position: relative; }
.ss-input {
  width: 100% !important;
  max-width: 100% !important;
  padding-right: 42px;
  cursor: pointer;
  background: #fff;
}
.ss.is-open .ss-input { cursor: text; }
.ss-control.errors .ss-input { border-color: #dc3545; }
.ss-caret {
  position: absolute; right: 14px; top: 50%; margin-top: -3px;
  border-left: 5px solid transparent; border-right: 5px solid transparent;
  border-top: 6px solid #8a94a6; pointer-events: none;
}
.ss.is-open .ss-caret { border-top-color: #0066b3; }
.ss-clear {
  position: absolute; right: 30px; top: 50%; transform: translateY(-50%);
  color: #8a94a6; font-size: 18px; line-height: 1; cursor: pointer;
}
.ss-clear:hover { color: #dc3545; }
.ss-menu {
  position: absolute; z-index: 1060; left: 0; right: 0; top: 100%;
  margin-top: 2px; max-height: 260px; overflow-y: auto;
  background: #fff; border: 1px solid #dfe3e8; border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}
.ss-option {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 12px; font-size: 14px; color: #2c3e50; cursor: pointer;
}
.ss-option.is-active { background: #f1f6fb; }
.ss-option.is-selected { font-weight: 600; color: #0066b3; }
.ss-option-note { color: #8a94a6; font-size: 12px; margin-left: 12px; white-space: nowrap; }
.ss-empty { padding: 12px; font-size: 13px; color: #8a94a6; }
.ss.is-disabled .ss-input { background: #f6f7f9; cursor: not-allowed; }
</style>
